/**
 * The postMessage protocol between the chatbot (inside the iframe) and the
 * embed script on the host page (`public/genesys.chatbot.js`).
 *
 * Only the standalone app deployment talks this protocol. Hosts that use the
 * npm package mount the widget straight into their own DOM, where the widget
 * positions itself with CSS and no bridge exists.
 */

import type { ScrollSubscription } from "../utils/teaserReveal";

export const HOST_MESSAGES = {
  /** App → host: geometry for the iframe element. */
  setIframeStyles: "SET_IFRAME_STYLES",
  /** App → host: "which page am I embedded in?" */
  requestParentUrl: "REQUEST_PARENT_URL",
  /** App → host: "how big is your viewport?" */
  requestWindowSize: "REQUEST_WINDOW_SIZE",
  /** App → host: "how far has your page been scrolled?" */
  requestScrollPosition: "REQUEST_SCROLL_POSITION",
  /** App → host: analytics event, pushed to the host's dataLayer. */
  chatEvent: "chat_event",
  /** Host → app: the host's hostname. */
  setParentUrl: "SET_PARENT_URL",
  /** Host → app: viewport size, also pushed on every host resize. */
  responseWindowSize: "RESPONSE_WINDOW_SIZE",
  /** Host → app: scroll position, also pushed on every host scroll. */
  responseScrollPosition: "RESPONSE_SCROLL_POSITION",
} as const;

export interface ParentViewport {
  width: number;
  height: number;
}

/**
 * Host domains the widget accepts a brand from. A page that is not listed can
 * still embed the widget — it just cannot tell us which brand it is, so the
 * configured/built-in brand stays in place.
 */
const trustedHostDomains: ReadonlyArray<string> = [
  "wbstraining.de",
  "wbs-gruppe.de",
  "wbs-schulen.de",
  "wbsakademie.de",
  "wbs-web.de",
  "localhost",
];

export const isTrustedHost = (hostname: string): boolean => {
  const host = hostname.toLowerCase();

  return trustedHostDomains.some(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  );
};

/** True when this document actually runs inside another window. */
export const isEmbeddedInIframe = (): boolean => {
  try {
    return globalThis.window !== undefined && window.parent !== window;
  } catch {
    // Cross-origin access to `window.parent` never throws for identity checks,
    // but a hostile sandbox might — treat that as "not embedded".
    return false;
  }
};

// The host origin is unknown to us (we may not read `parent.location`), and the
// embed script validates the sender before acting on anything. Nothing sent
// here is confidential: geometry and an analytics event name.
const postToHost = (message: Record<string, unknown>): void => {
  if (!isEmbeddedInIframe()) return;

  window.parent.postMessage(message, "*");
};

export const requestParentUrl = (): void =>
  postToHost({ type: HOST_MESSAGES.requestParentUrl });

export const requestWindowSize = (): void =>
  postToHost({ type: HOST_MESSAGES.requestWindowSize });

/**
 * Scroll source for the iframe deployment: the page that scrolls is the host,
 * which reports it through the embed script. Host pages still running an older
 * embed script never answer, so the teaser never appears there.
 */
export const subscribeToHostScroll: ScrollSubscription = (listener) => {
  const onHostMessage = (event: MessageEvent): void => {
    if (event.data?.type !== HOST_MESSAGES.responseScrollPosition) return;

    const { scrollY, viewportHeight, documentHeight } = event.data;

    if (
      typeof scrollY !== "number" ||
      typeof viewportHeight !== "number" ||
      typeof documentHeight !== "number"
    ) {
      return;
    }

    listener({ scrollY, viewportHeight, documentHeight });
  };

  window.addEventListener("message", onHostMessage);
  postToHost({ type: HOST_MESSAGES.requestScrollPosition });

  return () => window.removeEventListener("message", onHostMessage);
};

export const sendChatEventToHost = (eventName: string): void =>
  postToHost({ type: HOST_MESSAGES.chatEvent, chat_event: eventName });

export interface IframeStyles {
  width: string;
  height: string;
  border: string;
  borderRadius: string;
  position: string;
  bottom: string;
  right: string;
  zIndex: number;
}

/**
 * Deliberately without `display` / `visibility`: those two keys make the embed
 * script take over focus management and mark the whole host page as `inert`
 * (see its `setChatOpen`). The widget handles its own focus, and the launcher
 * bubble must never make the host page inert — so geometry only, exactly as
 * before the package rewrite.
 */
export const sendIframeStyles = (styles: IframeStyles): void =>
  postToHost({ type: HOST_MESSAGES.setIframeStyles, styles });
